import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useRememberedState, useRememberedScroll } from '../hooks/useRemembered';
import { Search, List, LayoutGrid, CalendarDays, Columns, RefreshCw, Inbox, Calendar, ChevronLeft, ChevronRight, AlertTriangle, GripVertical, User, Users, ShieldCheck, GraduationCap, ChevronUp, ChevronDown, Globe } from 'lucide-react';
import { UserProfile, ProjectViewSettings, ReportMetadata } from '../types';
import { inspectionRowsTotals } from '../services/inspectionFigures';
import { getServiceLog } from '../services/serviceJournal';
import { ProjectCard } from './ProjectCard';
import { cardSubstitution } from '../services/substitutionCard';
import { ProjectListItem } from './ProjectListItem';
import { KanbanBoard } from './KanbanBoard';
import { useProjectData, canShowAllProjects, resolveShowAll, ScopeUser } from '../hooks/useProjectData';
import { useRechte } from '../hooks/useRechte';
import { supabase } from '../services/supabaseClient';
import { patchProfileSettings } from '../services/cloudService';
import { getEffectiveDueDate } from '../services/dateService';
import { safeGetJSON, safeSetJSON } from '../services/safeStorage';
import { useProjectLocks } from '../hooks/useProjectLocks';
import { ProjectLockHint } from './ProjectLockHint';
import { istUebungsprojekt } from '../services/uebung/uebungService';
import { isProjectFinished } from '../services/projectBilling';
import type { ListProject } from '../services/listProject';

interface ProjectOverviewProps {
    projects: ListProject[];
    userProfile: UserProfile | null;
    onOpenProject: (project: ListProject) => void;
    onRefresh: () => void;
    /** Speichert den Folgetermin (App.handlePostpone); `true` = gespeichert. */
    onPostpone: (project: ListProject, days?: number) => Promise<boolean>;
    /** Speichert den naechsten Turnustermin (App.handleSkip); `true` = gespeichert. */
    onSkip: (project: ListProject) => Promise<boolean>;
}

/** Eintrag im Wochenkalender: der Listeneintrag, ueberfaellige als Kopie mit Merker in der Montagsspalte. */
type CalendarDayProject = ListProject & { _overdue?: boolean };

/**
 * Gemerkte Ansicht plus Chip „Alle".
 *
 * `showAllProjects` steht NEBEN `viewFilters`, nicht darin: die persönliche
 * Auswahl (Eigene/Vertretung/Joint) bleibt unter „Alle" unverändert liegen und
 * kommt beim Abwählen genau so zurück. Gelesen wird der Wert nur über
 * `resolveShowAll` — ohne das Recht `projekte.alle_sehen` ist er wirkungslos.
 */
type StoredProjectView = ProjectViewSettings & { showAllProjects?: boolean };

const STORAGE_KEY = 'sigeko_project_view_settings_v7';
const DEFAULT_SETTINGS: StoredProjectView = {
    viewFilters: { showOwn: true, showSubstitute: true, showDeputy: true },
    viewLayout: 'grid',
    viewGrouping: 'none',
    statusFilter: 'active_all',
    sortConfig: { key: 'date', direction: 'asc' },
    showAllProjects: false
};

// Die Chips, die es noch gibt. Ein gemerkter Filter aus der Zeit, als die
// Liste noch „Inkl. Archiv" kannte ('all', 'completed', 'finished'), fällt
// auf „Alle aktiven" zurück — sonst stünde kein Chip mehr markiert.
const STATUS_FILTER_IDS = ['active_all', 'active', 'planning', 'on_hold'] as const;
const sanitizeStatusFilter = (f: StoredProjectView['statusFilter'] | undefined): StoredProjectView['statusFilter'] =>
    f && (STATUS_FILTER_IDS as readonly string[]).includes(f) ? f : 'active_all';

const WEEKDAYS_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// ── Benutzerliste für „Alle" ─────────────────────────────────────────────
// Unter „Alle" stehen die Projekte der eigenen Firma — dieselbe Regel wie
// die Projektverwaltung (AdminView `filteredProjects`), und die braucht die
// Profile, um den Besitzer eines Projekts einer Firma zuzuordnen. App.tsx
// reicht keine Benutzerliste herein; geladen wird deshalb hier: für jeden
// außer super_admin (der braucht sie nicht), nur wenn „Alle" an ist, nur die
// drei Spalten der Firmenzuordnung — und modulweit zwischengespeichert, weil
// jeder Ansichtswechsel ProjectOverview neu aufbaut.
const SCOPE_USERS_TTL_MS = 10 * 60 * 1000;
let scopeUsersCache: { viewerId: string; at: number; users: ScopeUser[] } | null = null;
let scopeUsersInflight: { viewerId: string; promise: Promise<ScopeUser[]> } | null = null;

/** Zwischengespeicherte Liste dieses Betrachters, auch wenn sie schon älter ist (für den ersten Aufbau). */
const peekScopeUsers = (viewerId?: string | null): ScopeUser[] | null =>
    viewerId && scopeUsersCache?.viewerId === viewerId ? scopeUsersCache.users : null;

const fetchScopeUsers = async (): Promise<ScopeUser[]> => {
    // Zwei Anläufe wie fetchCompanyRows (services/companyInvoiceConfig.ts):
    // `profiles.company_id` entsteht erst mit companies.sql (#9). Fehlt die
    // Spalte (42703), trägt der Name allein — sameCompany vergleicht dann
    // ohnehin die Namen.
    const withId = await supabase.from('profiles').select('id, company_id, company_name');
    if (!withId.error) return (withId.data ?? []) as unknown as ScopeUser[];
    const legacy = await supabase.from('profiles').select('id, company_name');
    if (legacy.error) throw legacy.error;
    return (legacy.data ?? []) as unknown as ScopeUser[];
};

const loadScopeUsers = (viewerId: string): Promise<ScopeUser[]> => {
    if (scopeUsersCache?.viewerId === viewerId && Date.now() - scopeUsersCache.at < SCOPE_USERS_TTL_MS) {
        return Promise.resolve(scopeUsersCache.users);
    }
    if (scopeUsersInflight?.viewerId === viewerId) return scopeUsersInflight.promise;
    const promise = fetchScopeUsers().then(users => {
        scopeUsersCache = { viewerId, at: Date.now(), users };
        return users;
    });
    scopeUsersInflight = { viewerId, promise };
    const clear = () => { if (scopeUsersInflight?.promise === promise) scopeUsersInflight = null; };
    promise.then(clear, clear);
    return promise;
};

export const ProjectOverview: React.FC<ProjectOverviewProps> = ({ projects: alleProjekte, userProfile, onOpenProject, onRefresh, onPostpone, onSkip }) => {

    // ÜBUNGSPROJEKTE STEHEN NICHT IN DER ARBEITSLISTE.
    //
    // Sie sind sichtbar, aber in einem eigenen, eingeklappten Abschnitt weiter
    // unten (docs/lernzentrum-tutorial-plan.md, 8.1). Der Grund ist derselbe
    // wie bei allen anderen Auswertungen: ein Übungsprojekt hat eine
    // Fälligkeit, einen Rhythmus und ein LV wie jedes echte — es würde in
    // Kalender, Wochenplan und Statuszählern mitlaufen und dort eine Arbeit
    // vortäuschen, die niemand zu tun hat.
    //
    // BEENDETE UND ARCHIVIERTE PROJEKTE AUCH NICHT (Betreiberentscheidung
    // 06.10.2026). Sie standen gedämpft in der Liste und ließen sich weiter
    // öffnen und bearbeiten. Die Arbeit ist vorbei; Schlussrechnung und
    // Archiv liegen bei der Buchhaltung (AdminAccountingView), die Gesamtsicht
    // bei der Projektverwaltung. Beide Statusquellen zählen (isProjectFinished).
    const projects = useMemo(() => alleProjekte.filter(p => !istUebungsprojekt(p) && !isProjectFinished(p)), [alleProjekte]);
    const uebungsProjekte = useMemo(() => alleProjekte.filter(p => istUebungsprojekt(p)), [alleProjekte]);
    const [uebungOffen, setUebungOffen] = useRememberedState('projects.uebung', false);

    const initialSettings = useMemo((): StoredProjectView => {
        const stored = safeGetJSON<Partial<StoredProjectView> | null>(STORAGE_KEY, null);
        if (stored) return { ...DEFAULT_SETTINGS, ...stored };
        return userProfile?.settings?.projectView ? { ...DEFAULT_SETTINGS, ...(userProfile.settings.projectView as StoredProjectView) } : DEFAULT_SETTINGS;
    }, [userProfile]);

    const [searchTerm, setSearchTerm] = useRememberedState('projects.search', '');
    const scrollRef = useRememberedScroll('projects.scroll');
    const [viewFilters, setViewFilters] = useState(initialSettings.viewFilters);
    const [viewLayout, setViewLayout] = useState(initialSettings.viewLayout);
    const [viewGrouping, setViewGrouping] = useState(initialSettings.viewGrouping);
    const [statusFilter, setStatusFilter] = useState(() => sanitizeStatusFilter(initialSettings.statusFilter));
    const [sortConfig, setSortConfig] = useState(initialSettings.sortConfig);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [calWeekOffset, setCalWeekOffset] = useRememberedState('projects.calWeek', 0);
    const [calDragOverDay, setCalDragOverDay] = useState<string | null>(null);
    const [calDraggingId, setCalDraggingId] = useState<string | null>(null);
    const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Chip „Alle" (Recht `projekte.alle_sehen`, ab Werk aus) ──
    // Gemerkt wird der WUNSCH, wirksam ist er nur mit dem Recht.
    const rechte = useRechte(userProfile?.role);
    const [showAllRequested, setShowAllRequested] = useState<boolean>(!!initialSettings.showAllProjects);
    const mayShowAll = canShowAllProjects(rechte, userProfile?.role);
    const showAll = resolveShowAll(showAllRequested, rechte, userProfile?.role);
    // Gespeichert wird der wirksame Wert, sobald Profil UND Rechte da sind:
    // ein verlorenes Recht (Rolle entzogen, anderes Konto auf demselben
    // Gerät) räumt den gemerkten Wunsch so beim nächsten Speichern ab.
    // Solange eins von beiden noch lädt, bleibt der Wunsch unangetastet —
    // bis zur Antwort gelten die Rechte der Systemrolle, und die kennt das
    // Recht aus einer Rolle noch nicht.
    const persistedShowAll = userProfile && rechte.geladen ? showAll : showAllRequested;

    const [scopeUsers, setScopeUsers] = useState<ScopeUser[] | null>(() => peekScopeUsers(userProfile?.id));
    const [scopeUsersError, setScopeUsersError] = useState(false);
    const [scopeReloadTick, setScopeReloadTick] = useState(0);
    const needsScopeUsers = showAll && userProfile?.role !== 'super_admin';
    useEffect(() => {
        if (!needsScopeUsers || !userProfile?.id) return;
        let cancelled = false;
        loadScopeUsers(userProfile.id)
            .then(users => { if (!cancelled) { setScopeUsers(users); setScopeUsersError(false); } })
            .catch(e => {
                console.error('ProjectOverview: Benutzerliste für „Alle" nicht geladen', e);
                if (!cancelled) setScopeUsersError(true);
            });
        return () => { cancelled = true; };
    }, [needsScopeUsers, userProfile?.id, scopeReloadTick]);

    // Persist settings
    useEffect(() => {
        const s: StoredProjectView = { viewFilters, viewLayout, viewGrouping, statusFilter, sortConfig, showAllProjects: persistedShowAll };
        safeSetJSON(STORAGE_KEY, s);
        if (userProfile) {
            if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(async () => {
                try {
                    const { data: p } = await supabase.from('profiles').select('settings').eq('id', userProfile.id).single();
                    const ex = p?.settings || {};
                    if (JSON.stringify(ex.projectView) !== JSON.stringify(s)) {
                        // Nur den eigenen Schluessel aendern: das ganze Objekt
                        // zurueckzuschreiben nahm eine gleichzeitig gespeicherte Optik
                        // oder Kartenfarbe wieder mit.
                        await patchProfileSettings(userProfile.id, { projectView: s });
                    }
                } catch {}
            }, 2000);
        }
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, [viewFilters, viewLayout, viewGrouping, statusFilter, sortConfig, persistedShowAll, userProfile]);

    // 'active_all' steht seit der Erweiterung in types.ts in derselben Union
    // wie im Hook — der Filter geht unveraendert durch, ohne Cast.
    const { processedProjects: displayProjects, counts, allScopeCount, allScopePending } = useProjectData({
        projects, userProfile, searchTerm,
        statusFilter,
        viewFilters, sortConfig,
        rechte, showAll, scopeUsers
    });

    // Persönliche Chips: unter „Alle" gedimmt. Ein Tipp darauf verlässt „Alle",
    // bringt die vorherige Auswahl zurück und schaltet den angetippten Chip
    // sicher ein — wer „Eigene" antippt, will Eigene sehen.
    const togglePersonalFilter = (key: 'showOwn' | 'showSubstitute' | 'showDeputy') => {
        if (showAll) {
            setShowAllRequested(false);
            setViewFilters(f => ({ ...f, [key]: true }));
            return;
        }
        setViewFilters(f => ({ ...f, [key]: !f[key] }));
    };
    const personalChipBase = 'min-h-[40px] min-w-[40px] md:min-h-0 md:min-w-0 justify-center p-1.5 rounded-lg border text-[11px] font-bold flex items-center gap-1 transition-all';
    const personalChipOff = 'bg-white/40 text-slate-500 border-white/60';
    const dimmedUnderAll = showAll ? ' opacity-40' : '';
    const personalTitle = (label: string) => showAll ? `${label} — „Alle" ist aktiv; antippen kehrt zur persönlichen Auswahl zurück` : label;
    const allChipTitle = userProfile?.role === 'super_admin'
        ? 'Alle Projekte (alle Firmen)'
        : 'Alle Projekte der Firma';

    // Wer sitzt gerade in welchem Projekt? Ein gebuendelter Abruf fuer die
    // sichtbare Liste — nicht einer je Zeile. Faellt er aus (Migration Nr. 33
    // noch nicht gefahren, offline), bleibt die Map leer und die Liste sieht
    // aus wie zuvor.
    const { locks, nowMs: lockNowMs } = useProjectLocks(displayProjects.map(p => p.id));

    const handleSort = (key: string) => setSortConfig(c => ({ key, direction: c.key === key && c.direction === 'asc' ? 'desc' : 'asc' }));
    const formatDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
    const handleRefresh = async () => { setIsRefreshing(true); await onRefresh(); setTimeout(() => setIsRefreshing(false), 600); };

    // Gespeichert wird NUR im App-Handler (onPostpone/onSkip): er laedt das
    // Projekt voll und aendert allein den Folgetermin. Bis zum 21.10.2026
    // speicherte diese Ansicht zusaetzlich selbst — den Listeneintrag, der
    // nur `metadata` traegt, als ganzes Projekt, und loeschte damit Unterlage,
    // Vorankuendigung und Einstellungen (listenAuszug.ts). Hier bleibt nur,
    // was die Ansicht selbst tut: nach Erfolg neu laden, sonst melden.
    const handlePostponeWrapped = async (project: ListProject, days = 7) => {
        if (await onPostpone(project, days)) handleRefresh();
        else alert('Der Termin konnte nicht verschoben werden. Bitte erneut versuchen.');
    };

    const handleSkipWrapped = async (project: ListProject) => {
        const meta = project.data.metadata as ReportMetadata;
        if (!meta.intervalConfig) return;
        if (await onSkip(project)) handleRefresh();
        else alert('Die Begehung konnte nicht übersprungen werden. Bitte erneut versuchen.');
    };

    // ── Outlook Calendar View ──
    const calendarData = useMemo(() => {
        const today = new Date();
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay() || 7; // Mo=1..Su=7
        startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek + 1 + calWeekOffset * 7);

        const days: { date: Date; dateStr: string; isToday: boolean; projects: CalendarDayProject[] }[] = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(startOfWeek);
            d.setDate(d.getDate() + i);
            const ds = d.toISOString().split('T')[0];
            const todayStr = today.toISOString().split('T')[0];
            days.push({ date: d, dateStr: ds, isToday: ds === todayStr, projects: [] });
        }

        // Place projects on their due date
        const activeProjects = displayProjects.filter(p => {
            const st = p.status || p.data?.metadata?.globalData?.status;
            return st === 'active';
        });

        activeProjects.forEach(p => {
            const edd = getEffectiveDueDate(p);
            if (!edd) return;
            const dueStr = new Date(edd).toISOString().split('T')[0];
            const day = days.find(d => d.dateStr === dueStr);
            if (day) day.projects.push(p);
        });

        // Also show overdue in Monday column
        activeProjects.forEach(p => {
            const edd = getEffectiveDueDate(p);
            if (!edd) return;
            const due = new Date(edd);
            if (due < days[0].date && calWeekOffset === 0) {
                days[0].projects.push({ ...p, _overdue: true });
            }
        });

        const weekLabel = `${days[0].date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} – ${days[6].date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`;
        return { days, weekLabel };
    }, [displayProjects, calWeekOffset]);

    // Status chips config
    // Ein Array-Literal weitet `id` auf `string` — der Cast unten bleibt
    // deshalb noetig (ohne ihn: TS2345, 'string' not assignable to
    // 'SetStateAction<"all" | "active" | … | "active_all">').
    const statusChips = [
        { id: 'active_all', label: 'Alle aktiven', count: (counts.active || 0) + (counts.planning || 0) + (counts.on_hold || 0) },
        { id: 'active', label: 'Ausführung', count: counts.active },
        { id: 'planning', label: 'Planung', count: counts.planning },
        { id: 'on_hold', label: 'Ruhend', count: counts.on_hold },
    ];

    return (
        <div data-glass-view="projekte" className="flex-1 flex flex-col overflow-hidden bg-transparent">

            {/* ── COMPACT HEADER ── */}
            <div data-glass="kopf" className="glass-panel border-b border-white/20 px-5 py-3 shrink-0 z-20">
                <div className="flex flex-col lg:flex-row gap-3 justify-between items-start lg:items-center">

                    {/* Left: Search + Status */}
                    <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto items-start sm:items-center">
                        <div className="relative w-full sm:w-52 group">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 group-focus-within:text-blue-500" />
                            <input type="search" name="projectSearch" autoComplete="off" placeholder="Suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-8 pr-3 py-2 bg-white/40 backdrop-blur-sm border border-white/60 rounded-lg text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 outline-none transition-all" />
                        </div>
                        <div data-glass="umschalter" className="flex items-center gap-1 overflow-x-auto no-scrollbar max-w-full">
                            {statusChips.map(s => (
                                <button key={s.id} onClick={() => setStatusFilter(s.id as any)} className={`whitespace-nowrap px-2.5 py-1.5 rounded-full text-[11px] font-bold border transition-all flex items-center gap-1 ${statusFilter === s.id ? 'bg-slate-800/90 backdrop-blur-sm text-white border-slate-700/50' : 'bg-white/40 text-slate-600 border-white/60 hover:bg-white/60'}`}>
                                    {s.label}
                                    {s.count > 0 && <span className={`text-[10px] px-1 rounded-full ${statusFilter === s.id ? 'bg-slate-600' : 'bg-white/80'}`}>{s.count}</span>}
                                </button>
                            ))}
                        </div>

                        <div className="h-4 w-px bg-white/50 mx-1 hidden sm:block" />

                        {/* Persönliche Auswahl + (mit Recht `projekte.alle_sehen`) „Alle".
                            Mobil 40 px Tippfläche, ab md die alte kompakte Größe;
                            die Reihe bricht um statt über den Rand zu laufen. */}
                        <div data-glass="umschalter" className="flex flex-wrap items-center gap-1 max-w-full" role="group" aria-label="Projektauswahl">
                            <button onClick={() => togglePersonalFilter('showOwn')} aria-pressed={!showAll && !!viewFilters.showOwn} className={`${personalChipBase} ${!showAll && viewFilters.showOwn ? 'bg-blue-500/10 text-blue-700 border-blue-200/50' : personalChipOff}${dimmedUnderAll}`} title={personalTitle('Meine Projekte')}>
                                <User className="w-3 h-3" /> <span className="hidden xl:inline">Eigene</span>
                            </button>
                            <button onClick={() => togglePersonalFilter('showSubstitute')} aria-pressed={!showAll && !!viewFilters.showSubstitute} className={`${personalChipBase} ${!showAll && viewFilters.showSubstitute ? 'bg-teal-500/10 text-teal-700 border-teal-200/50' : personalChipOff}${dimmedUnderAll}`} title={personalTitle('Vertretungen')}>
                                <ShieldCheck className="w-3 h-3" /> <span className="hidden xl:inline">Vertretung</span>
                            </button>
                            <button onClick={() => togglePersonalFilter('showDeputy')} aria-pressed={!showAll && !!viewFilters.showDeputy} className={`${personalChipBase} ${!showAll && viewFilters.showDeputy ? 'bg-indigo-500/10 text-indigo-700 border-indigo-200/50' : personalChipOff}${dimmedUnderAll}`} title={personalTitle('Zweitkoordinatoren (Gemeinsam)')}>
                                <Users className="w-3 h-3" /> <span className="hidden xl:inline">Joint</span>
                            </button>
                            {mayShowAll && (
                                <>
                                    <div className="h-5 w-px bg-white/50 mx-0.5" aria-hidden="true" />
                                    <button
                                        onClick={() => { setScopeUsersError(false); setShowAllRequested(!showAll); }}
                                        aria-pressed={showAll}
                                        title={showAll ? `${allChipTitle} — antippen kehrt zur persönlichen Auswahl zurück` : allChipTitle}
                                        className={`${personalChipBase} px-2.5 ${showAll ? 'bg-slate-800/90 backdrop-blur-sm text-white border-slate-700/50 shadow-sm' : 'bg-white/40 text-slate-600 border-white/60 hover:bg-white/60'}`}
                                    >
                                        <Globe className="w-3 h-3" /> <span>Alle</span>
                                        {allScopePending && !scopeUsersError
                                            ? <RefreshCw className="w-3 h-3 animate-spin" aria-label="lädt" />
                                            : allScopeCount !== null && <span className={`text-[10px] px-1 rounded-full ${showAll ? 'bg-slate-600' : 'bg-white/80'}`}>{allScopeCount}</span>}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Right: View switches */}
                    <div className="flex items-center gap-2 shrink-0">
                        <div data-glass="umschalter" className="flex bg-white/30 backdrop-blur-sm p-0.5 rounded-lg border border-white/50">
                            <button onClick={() => { setViewGrouping('none'); setViewLayout('grid'); }} className={`p-2.5 md:p-1.5 rounded-md text-xs transition-all ${viewGrouping === 'none' && viewLayout === 'grid' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500 hover:text-slate-700'}`} title="Kacheln"><LayoutGrid className="w-3.5 h-3.5" /></button>
                            <button onClick={() => { setViewGrouping('none'); setViewLayout('list'); }} className={`p-2.5 md:p-1.5 rounded-md text-xs transition-all ${viewGrouping === 'none' && viewLayout === 'list' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500 hover:text-slate-700'}`} title="Liste"><List className="w-3.5 h-3.5" /></button>
                            <button onClick={() => { setViewGrouping('calendar'); setCalWeekOffset(0); }} className={`p-2.5 md:p-1.5 rounded-md text-xs transition-all ${viewGrouping === 'calendar' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500 hover:text-slate-700'}`} title="Kalender"><Calendar className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setViewGrouping('weekday')} className={`p-2.5 md:p-1.5 rounded-md text-xs transition-all ${viewGrouping === 'weekday' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500 hover:text-slate-700'}`} title="Wochenplan"><CalendarDays className="w-3.5 h-3.5" /></button>
                            <button onClick={() => setViewGrouping('interval')} className={`p-2.5 md:p-1.5 rounded-md text-xs transition-all ${viewGrouping === 'interval' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500 hover:text-slate-700'}`} title="Intervall"><Columns className="w-3.5 h-3.5" /></button>
                        </div>
                        <button onClick={handleRefresh} disabled={isRefreshing} className="w-8 h-8 flex items-center justify-center rounded-lg border border-white/60 bg-white/40 backdrop-blur-sm text-slate-500 hover:text-blue-600 hover:border-blue-200/50 disabled:opacity-50 transition-all shrink-0 shadow-sm">
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
                {/* „Alle" gewählt, Benutzerliste fehlt: offen sagen, dass die Liste
                    gerade NICHT die Firma zeigt — sonst hält man die persönliche
                    Auswahl für den Gesamtbestand. */}
                {showAll && scopeUsersError && (
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-amber-800 bg-amber-50/80 border border-amber-200 rounded-lg px-2.5 py-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span className="flex-1 min-w-0">Benutzerliste nicht geladen — „Alle" zeigt vorerst nur die persönliche Auswahl.</span>
                        <button onClick={() => { setScopeUsersError(false); setScopeReloadTick(t => t + 1); }} className="font-bold underline min-h-[40px] md:min-h-0 px-1">Erneut versuchen</button>
                    </div>
                )}
            </div>

            {/* ── CONTENT ── */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5">

                {/* Calendar View — Drag & Drop enabled */}
                {viewGrouping === 'calendar' && (
                    <div className="space-y-3">
                        {/* Week navigation */}
                        <div className="flex items-center justify-between bg-white rounded-xl border border-slate-200 px-4 py-2.5">
                            <button onClick={() => setCalWeekOffset(w => w - 1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronLeft className="w-4 h-4" /></button>
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-bold text-slate-700">{calendarData.weekLabel}</span>
                                {calWeekOffset !== 0 && <button onClick={() => setCalWeekOffset(0)} className="text-[11px] font-bold text-blue-600 hover:text-blue-800 px-2 py-0.5 bg-blue-50 rounded-full border border-blue-200">Heute</button>}
                            </div>
                            <button onClick={() => setCalWeekOffset(w => w + 1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500"><ChevronRight className="w-4 h-4" /></button>
                        </div>

                        {/* Day columns with position-based Drag & Drop */}
                        <div
                            className="grid grid-cols-7 gap-2"
                            ref={el => { if (el) (el as any).__calGridRef = el; }}
                            onDragOver={e => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                // Calculate which column based on mouse X position
                                const grid = e.currentTarget as HTMLElement;
                                const rect = grid.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const colWidth = rect.width / 7;
                                const colIdx = Math.max(0, Math.min(6, Math.floor(x / colWidth)));
                                const targetDay = calendarData.days[colIdx];
                                if (targetDay && calDragOverDay !== targetDay.dateStr) {
                                    setCalDragOverDay(targetDay.dateStr);
                                }
                            }}
                            onDragLeave={e => {
                                // Only clear if actually leaving the grid
                                const rect = e.currentTarget.getBoundingClientRect();
                                if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
                                    setCalDragOverDay(null);
                                }
                            }}
                            onDrop={e => {
                                e.preventDefault();
                                // Calculate target column from drop position
                                const grid = e.currentTarget as HTMLElement;
                                const rect = grid.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                const colWidth = rect.width / 7;
                                const colIdx = Math.max(0, Math.min(6, Math.floor(x / colWidth)));
                                const targetDay = calendarData.days[colIdx];

                                setCalDragOverDay(null);
                                setCalDraggingId(null);

                                if (!targetDay) return;
                                const projectId = e.dataTransfer.getData('text/plain');
                                if (!projectId) return;
                                const project = displayProjects.find(p => p.id === projectId);
                                if (!project) return;

                                const currentDue = getEffectiveDueDate(project);
                                const currentDate = currentDue ? new Date(currentDue) : new Date();
                                currentDate.setHours(0, 0, 0, 0);
                                const targetDate = new Date(targetDay.dateStr);
                                targetDate.setHours(0, 0, 0, 0);
                                const diffDays = Math.round((targetDate.getTime() - currentDate.getTime()) / 864e5);
                                if (diffDays !== 0) handlePostponeWrapped(project, diffDays);
                            }}
                        >
                            {calendarData.days.map((day, i) => {
                                const isWeekend = i >= 5;
                                const isDropTarget = calDragOverDay === day.dateStr;
                                return (
                                    <div
                                        key={day.dateStr}
                                        className={`rounded-xl border min-h-[250px] flex flex-col transition-all duration-150 pointer-events-auto ${
                                            isDropTarget ? 'border-blue-400 bg-blue-50/50 ring-2 ring-blue-200 scale-[1.02]'
                                            : day.isToday ? 'border-blue-300 bg-blue-50/20 ring-1 ring-blue-100'
                                            : isWeekend ? 'border-slate-200 bg-slate-50/30'
                                            : 'border-slate-200 bg-white'
                                        }`}
                                    >
                                        {/* Day header */}
                                        <div className={`px-2.5 py-2 border-b text-center ${day.isToday ? 'border-blue-200 bg-blue-50' : isDropTarget ? 'border-blue-200 bg-blue-50' : 'border-slate-100'}`}>
                                            <div className={`text-[11px] font-bold uppercase ${day.isToday ? 'text-blue-600' : isWeekend ? 'text-slate-400' : 'text-slate-500'}`}>{WEEKDAYS_SHORT[i]}</div>
                                            <div className={`text-lg font-black ${day.isToday ? 'text-blue-600' : 'text-slate-700'}`}>{day.date.getDate()}</div>
                                            {day.projects.length > 0 && <div className="text-[10px] text-slate-400 font-bold">{day.projects.length} Projekt{day.projects.length !== 1 ? 'e' : ''}</div>}
                                        </div>

                                        {/* Project cards with drag handle */}
                                        <div className="flex-1 p-1.5 space-y-1.5 overflow-y-auto">
                                            {day.projects.length === 0 && (
                                                <div className={`h-20 border-2 border-dashed rounded-lg flex items-center justify-center text-[11px] italic ${isDropTarget ? 'border-blue-300 bg-blue-50/50 text-blue-400' : 'border-slate-200 text-slate-300'}`}>
                                                    {isDropTarget ? 'Hier ablegen' : '—'}
                                                </div>
                                            )}
                                            {day.projects.map((p, pi) => {
                                                const isOverdue = p._overdue;
                                                const meta = p.data?.metadata as ReportMetadata | undefined;
                                                const dbNr = p.current_inspection_number || 0;
                                                const projectNumber = p.project_number || meta?.projectNumber;
                                                const pStatus = p.status || meta?.globalData?.status || 'planning';
                                                const isDragging = calDraggingId === p.id;
                                                const isDraggable = pStatus === 'active';

                                                // Inspection progress — ohne Monatspauschale, mit den Nachweisen
                                                // auf der Pauschale (Pauschalprojekt: die Begehungsposition waechst
                                                // dort nicht mehr). Protokoll aus der Listenzeile, nichts nachgeladen.
                                                const { used: inspUs, contracted: inspCt } = inspectionRowsTotals(p.project_services, getServiceLog(p.data));
                                                const inspPct = inspCt > 0 ? Math.min(100, Math.round(inspUs / inspCt * 100)) : -1;

                                                return (
                                                    <div
                                                        key={`${p.id}-${pi}`}
                                                        draggable={isDraggable}
                                                        onDragStart={e => { e.dataTransfer.setData('text/plain', p.id); e.dataTransfer.effectAllowed = 'move'; setCalDraggingId(p.id); }}
                                                        onDragEnd={() => { setCalDraggingId(null); setCalDragOverDay(null); }}
                                                        className={`rounded-lg border shadow-sm hover:shadow-md transition-all group relative ${
                                                            isOverdue ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-white'
                                                        } ${isDragging ? 'opacity-30 scale-95 rotate-1' : ''} ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                                    >
                                                        {/* Drag handle */}
                                                        {isDraggable && (
                                                            <div className="absolute left-0 top-0 bottom-0 w-4 flex items-center justify-center opacity-0 group-hover:opacity-30 hover:!opacity-80 text-slate-400">
                                                                <GripVertical className="w-2.5 h-2.5" />
                                                            </div>
                                                        )}

                                                        <div onClick={() => onOpenProject(p)} className={`cursor-pointer p-2 ${isDraggable ? 'pl-4' : ''}`}>
                                                            {isOverdue && <div className="flex items-center gap-1 text-[10px] font-bold text-red-600 bg-red-100 px-1 py-0.5 rounded mb-1 w-fit border border-red-200"><AlertTriangle className="w-2 h-2" />Überfällig</div>}
                                                            <div className="font-bold text-[11px] text-slate-800 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">{p.name}</div>
                                                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                                {projectNumber && <span className="font-mono">#{projectNumber}</span>}
                                                                <span>· Nr.{dbNr}</span>
                                                            </div>
                                                            <ProjectLockHint lock={locks.get(p.id)} currentUserId={userProfile?.id} nowMs={lockNowMs} compact className="mt-1" />
                                                            {inspPct >= 0 && (
                                                                <div className="mt-1.5">
                                                                    <div className="flex justify-between text-[10px] mb-0.5"><span className="text-slate-400">{inspUs}/{inspCt}</span></div>
                                                                    <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${inspPct >= 90 ? 'bg-emerald-500' : inspPct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`} style={{ width: `${inspPct}%` }} /></div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Quick actions */}
                                                        <div className="flex gap-0.5 px-1.5 py-1 border-t border-slate-100 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                                            <button onClick={e => { e.stopPropagation(); handlePostponeWrapped(p, 7); }} className="flex-1 text-center py-0.5 rounded text-[10px] font-bold text-slate-400 hover:bg-amber-50 hover:text-amber-600 transition-colors">+7d</button>
                                                            <button onClick={e => { e.stopPropagation(); handlePostponeWrapped(p, -7); }} className="flex-1 text-center py-0.5 rounded text-[10px] font-bold text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors">-7d</button>
                                                            <button onClick={e => { e.stopPropagation(); handleSkipWrapped(p); }} className="flex-1 text-center py-0.5 rounded text-[10px] font-bold text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">Skip</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Unscheduled projects below */}
                        {(() => {
                            const scheduled = new Set(calendarData.days.flatMap(d => d.projects.map(p => p.id)));
                            const unscheduled = displayProjects.filter(p => {
                                const st = p.status || p.data?.metadata?.globalData?.status;
                                return st === 'active' && !scheduled.has(p.id);
                            });
                            if (!unscheduled.length) return null;
                            return (
                                <div className="mt-4">
                                    <div data-glass="auf-fenster" className="text-[11px] font-bold text-slate-400 uppercase mb-2 px-1">Nicht in dieser Woche ({unscheduled.length})</div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {unscheduled.slice(0, 12).map(p => (
                                            <ProjectCard key={p.id} project={p} substitution={cardSubstitution(p, userProfile?.email)} onClick={onOpenProject} onPostpone={handlePostponeWrapped} onSkip={handleSkipWrapped} formatDate={formatDate} lock={locks.get(p.id)} currentUserId={userProfile?.id} lockNowMs={lockNowMs} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                )}

                {/* Kanban views */}
                {(viewGrouping === 'weekday' || viewGrouping === 'interval') && (
                    <KanbanBoard
                        projects={displayProjects}
                        grouping={viewGrouping === 'weekday' ? 'weekday' : 'interval'}
                        viewMode="standard"
                        weekOffset={0}
                        onWeekChange={() => {}}
                        onOpenProject={onOpenProject}
                        onPostpone={handlePostponeWrapped}
                        onSkip={handleSkipWrapped}
                        locks={locks}
                        currentUserId={userProfile?.id}
                        lockNowMs={lockNowMs}
                    />
                )}

                {/* Grid / List */}
                {viewGrouping === 'none' && (
                    displayProjects.length === 0 ? (
                        <div data-glass="auf-fenster" className="flex flex-col items-center justify-center h-full text-center py-20">
                            <div className="bg-slate-100 p-5 rounded-full mb-3"><Inbox className="w-10 h-10 text-slate-300" /></div>
                            <h3 className="text-base font-bold text-slate-600">Keine Projekte gefunden</h3>
                            <p className="text-slate-400 text-xs mt-1 max-w-xs">Passen Sie die Filter an oder erstellen Sie ein neues Projekt.</p>
                            <button onClick={() => { setSearchTerm(''); setStatusFilter('active_all'); }} className="mt-4 text-blue-600 font-bold text-xs hover:underline">Filter zurücksetzen</button>
                        </div>
                    ) : viewLayout === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 pb-20">
                            {displayProjects.map(p => (
                                <ProjectCard key={p.id} project={p} substitution={cardSubstitution(p, userProfile?.email)} onClick={onOpenProject} onPostpone={handlePostponeWrapped} onSkip={handleSkipWrapped} formatDate={formatDate} lock={locks.get(p.id)} currentUserId={userProfile?.id} lockNowMs={lockNowMs} />
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-20">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase text-[11px] sticky top-0 z-10">
                                    <tr>
                                        <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('status')}>Status</th>
                                        <th className="px-4 py-3 w-1/3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('projectNumber')}>Projekt</th>
                                        <th className="px-4 py-3 cursor-pointer hover:bg-slate-100" onClick={() => handleSort('date')}>Letzte Beg.</th>
                                        <th className="px-4 py-3">Nächste</th>
                                        <th className="px-4 py-3">Fortschritt</th>
                                        <th className="px-4 py-3 text-right">Aktion</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {displayProjects.map(p => <ProjectListItem key={p.id} project={p} onClick={onOpenProject} formatDate={formatDate} lock={locks.get(p.id)} currentUserId={userProfile?.id} lockNowMs={lockNowMs} />)}
                                </tbody>
                            </table>
                        </div>
                    )
                )}

                {/* ── MEINE ÜBUNGSPROJEKTE ──────────────────────────────────
                    Eingeklappt und ganz unten: sie gehören zum Lernzentrum,
                    nicht zur Arbeit des Tages. Sichtbar bleiben sie trotzdem,
                    weil ein Projekt, das nur über eine andere Ansicht
                    erreichbar ist, irgendwann vergessen wird — und dann als
                    „verschwundenes Projekt" gemeldet wird. */}
                {uebungsProjekte.length > 0 && (
                    <div className="mt-6 mb-20">
                        <button
                            onClick={() => setUebungOffen(o => !o)}
                            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 transition-colors"
                        >
                            <GraduationCap className="w-4 h-4 shrink-0" />
                            <span className="text-xs font-bold">Meine Übungsprojekte</span>
                            <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-white border border-amber-200">{uebungsProjekte.length}</span>
                            <span className="flex-1" />
                            {uebungOffen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {uebungOffen && (
                            <div className="mt-3 bg-white border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                                {uebungsProjekte.map(p => (
                                    <button
                                        key={p.id}
                                        onClick={() => onOpenProject(p)}
                                        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-amber-50/60 transition-colors"
                                    >
                                        <span className="text-[10px] font-black tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">ÜBUNG</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-bold text-slate-700 truncate">{p.name}</span>
                                            <span className="block text-[11px] text-slate-400">{p.project_number || '—'}</span>
                                        </span>
                                        <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">{formatDate(p.updated_at)}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};